import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HiOutlineExternalLink } from 'react-icons/hi';
import MdlCard from './MdlCard';
import MdlFieldRow from './MdlFieldRow';
import MdlConnectors, { Connection } from './MdlConnectors';
import {
    Routing,
    MessageComposition,
    Template,
    CHANNEL_LABELS,
    CHANNEL_ADAPTER,
} from '../../../../types/communication';
import { templateLabel } from '../composition/studio/studioDraft';

interface RoutingDiagramProps {
    routing: Routing;
    messageCompositions: MessageComposition[];
    templates: Template[];
}

// Desenho do fluxo do dado (estilo MDL): Evento → Regra de roteamento (match +
// condições + mapeamento por canal) → Modelo/Templates por canal, com conectores
// ligando campo do evento → variável → variável do template.
const RoutingDiagram: React.FC<RoutingDiagramProps> = ({ routing, messageCompositions, templates }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const eventRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const ruleRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const varRefs = useRef<Record<string, HTMLDivElement | null>>({});

    const [ready, setReady] = useState(0);
    useEffect(() => {
        const id = window.setTimeout(() => setReady((n) => n + 1), 80);
        return () => window.clearTimeout(id);
    }, [routing.id, messageCompositions.length, templates.length]);

    // chave estável por target+variável para refs do bloco "regra"
    const ruleKey = (targetIdx: number, variableName: string) => `${targetIdx}:${variableName}`;

    // A chave do variableMapping (ex. amount, dueDate) pode sofrer derivação antes de
    // alimentar o template (amount->amountFormatted, dueDate->dueDateFormatted). O elo com a
    // variável do template é o atributo `field`; normalizamos a derivação aqui.
    const mappingToField = (variableName: string): string => {
        if (variableName === 'amount') return 'amountFormatted';
        if (variableName === 'dueDate' || variableName === 'effectiveDueDate') return 'dueDateFormatted';
        return variableName;
    };

    const eventFields = useMemo(
        () =>
            Array.from(
                new Set(routing.targets.flatMap((t) => t.fieldMappings.map((m) => m.eventField))),
            ),
        [routing],
    );

    const targetViews = useMemo(
        () =>
            routing.targets.map((target) => {
                const meta = messageCompositions.find((m) => m.id === target.messageCompositionId) || null;
                const embedded = meta?.templates ?? [];
                const tpls = embedded.length
                    ? embedded
                    : meta
                      ? templates.filter((t) => meta.templateIds.includes(t.id))
                      : [];
                return { target, meta, templates: tpls };
            }),
        [routing, messageCompositions, templates],
    );

    const connections: Connection[] = useMemo(() => {
        const list: Connection[] = [];
        routing.targets.forEach((target, ti) => {
            target.fieldMappings.forEach((m) => {
                // evento -> regra (variável do target)
                const evEl = eventRefs.current[m.eventField];
                const ruleEl = ruleRefs.current[ruleKey(ti, m.variableName)];
                if (evEl && ruleEl) list.push({ from: evEl, to: ruleEl });
                // regra -> variável do template no canal (liga pelo `field`)
                const view = targetViews[ti];
                const targetField = mappingToField(m.variableName);
                view?.templates.forEach((t) => {
                    const vEl = varRefs.current[`${t.id}:${targetField}`];
                    if (ruleEl && vEl) list.push({ from: ruleEl, to: vEl });
                });
            });
        });
        return list;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routing, targetViews, ready]);

    return (
        <div ref={containerRef} className="relative p-4">
            <MdlConnectors containerRef={containerRef} connections={connections} revision={ready} />
            <div className="grid grid-cols-3 gap-10 mb-3">
                <div className="rounded-md bg-gray-100 dark:bg-slate-700/50 px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-300">
                    Evento · o que acontece
                </div>
                <div className="rounded-md bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-300">
                    Rota · regra de roteamento
                </div>
                <div className="rounded-md bg-emerald-50 dark:bg-emerald-900/30 px-3 py-1.5 text-center text-[11px] font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    Composição · seus modelos (templates)
                </div>
            </div>
            <div className="relative grid grid-cols-3 gap-10">

            {/* Coluna 1 — Evento */}
            <div className="relative flex flex-col gap-4" style={{ zIndex: 2 }}>
                <MdlCard title={routing.event} subtitle="fato da empresa · evento de domínio" accent="gray">
                    {eventFields.map((f) => (
                        <MdlFieldRow key={f} label={f} ref={(el) => (eventRefs.current[f] = el)} />
                    ))}
                </MdlCard>
            </div>

            {/* Coluna 2 — Regra de roteamento (um bloco por target/canal) */}
            <div className="relative flex flex-col gap-6" style={{ zIndex: 2 }}>
                {targetViews.map(({ target, meta }, ti) => (
                    <MdlCard
                        key={ti}
                        title={CHANNEL_LABELS[target.channel]}
                        subtitle={meta?.name ?? '— composição não encontrada'}
                        href={meta ? `/communication/message-composition/${meta.id}` : undefined}
                        accent="blue"
                    >
                        <MdlFieldRow
                            label="envio"
                            value={
                                target.sendMode === 'SCHEDULED'
                                    ? `agendado ${target.scheduledAt ?? ''}`
                                    : 'imediato'
                            }
                            mono={false}
                        />
                        <MdlFieldRow
                            label="agrupamento"
                            value={target.groupByPayer ? 'agrupa por pagador' : 'individual'}
                            mono={false}
                        />
                        {target.groupName && (
                            <MdlFieldRow label="grupo" value={target.groupName} mono={false} />
                        )}
                        <MdlFieldRow
                            label="limites"
                            value={`máx ${target.limits.maxItems} · ${target.limits.truncateStrategy}`}
                            mono={false}
                        />
                        <div className="my-2 border-t border-dashed border-gray-200" />
                        <div className="text-[11px] font-semibold text-gray-400 mb-1">Mapeamento de variáveis</div>
                        {target.fieldMappings.length === 0 && (
                            <p className="text-[11px] text-gray-500">
                                Sem mapeamento declarativo — preenchido pelo serviço.
                            </p>
                        )}
                        {target.fieldMappings.map((m) => (
                            <MdlFieldRow
                                key={m.variableName}
                                label={m.variableName}
                                value={`← ${m.eventField}`}
                                ref={(el) => (ruleRefs.current[ruleKey(ti, m.variableName)] = el)}
                            />
                        ))}
                    </MdlCard>
                ))}
            </div>

            {/* Coluna 3 — Modelo/Templates por canal */}
            <div className="relative flex flex-col gap-6" style={{ zIndex: 2 }}>
                {targetViews.map(({ target, meta, templates: tpls }, ti) => (
                    <MdlCard
                        key={ti}
                        title={meta?.name ?? 'Composição não encontrada'}
                        href={meta ? `/communication/message-composition/${meta.id}` : undefined}
                        subtitle={`${CHANNEL_LABELS[target.channel]} · ${CHANNEL_ADAPTER[target.channel]}`}
                        accent="green"
                    >
                        {tpls.length === 0 && <p className="text-xs text-gray-500">Sem modelos.</p>}
                        {tpls.map((t) => (
                            <div key={t.id} className="mb-3 last:mb-0">
                                <Link
                                    to={`/communication/message-composition/${meta?.id}?template=${t.id}`}
                                    className="group inline-flex items-center gap-1 text-[11px] font-bold text-gray-700 mb-1 hover:text-teal-700 hover:underline"
                                    title="Abrir este modelo"
                                >
                                    {templateLabel(t)}
                                    <HiOutlineExternalLink size={12} className="opacity-40 group-hover:opacity-100" />
                                </Link>
                                {t.variables.map((v) => (
                                    <MdlFieldRow
                                        key={v.name}
                                        label={v.name}
                                        value={v.label}
                                        ref={(el) => (varRefs.current[`${t.id}:${v.field ?? v.name}`] = el)}
                                    />
                                ))}
                            </div>
                        ))}
                    </MdlCard>
                ))}
            </div>
            </div>
        </div>
    );
};

export default RoutingDiagram;
